import {
  Box,
  Flex,
  HStack,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import type React from 'react';
import { useLocation } from 'react-router-dom';

import { useCustomSkillLevelLabels } from '../../../../shared/selection-lists/hooks/useCustomSkillLevelLabels';
import {
  SKILL_TAXONOMY_SELECTION_LIST_KEYS,
  type SkillTaxonomySelectionListKey,
} from '../../../../shared/selection-lists/types';
import { useCurrencyPreference } from '../../../../shared/user-preferences/hooks/useCurrencyPreference';
import {
  formatFilterCurrencyValue,
  isFundingFilterId,
  isRateFilterId,
} from '../../../../shared/user-preferences/utils/currency-formatting';
import { shouldShowCurrentPreviousToggle } from '../config/currentPreviousConfig';
import { useFilterContext } from '../contexts/FilterProvider';
import { useAvailableDateRangeWithTrialLag } from '../hooks/useAvailableDateRangeWithTrialLag';
import { useViewDateGranularity } from '../hooks/useViewDateGranularity';
import {
  type Filter,
  FilterId,
  FilterType,
  isMultiFilterInstance,
} from '../types/filters';
import {
  isMinEffectivelySet as checkIsMinEffectivelySet,
  isMaxEffectivelySet,
  isYearFilter,
} from '../utils/common';
import { getFormattedDateString } from '../utils/dateUtils';
import { FilterPopover } from './FilterPopover';

export interface FilterTagProps {
  filter: Filter;
  onRemoveFilter: (filterId: FilterId) => void;
  disabled?: boolean;
  hideDisabledTooltip?: boolean;
}

// Format a list of values as a comma separated string +N more
const formatValueList = (values: string[]): string | null => {
  const count = values.length;

  if (count === 0) {
    return null;
  }

  if (count <= 1) {
    return values[0] ?? null;
  }

  if (count <= 3) {
    return values.join(', ');
  }

  const displayed = values.slice(0, 3).join(', ');
  const remainingCount = count - 3;

  return `${displayed} +${remainingCount} more`;
};

const getFilterItemCount = (filter: Filter): number => {
  if ('selections' in filter && Array.isArray(filter.selections)) {
    return filter.selections.length;
  }

  if ('text' in filter && Array.isArray(filter.text)) {
    return filter.text.length;
  }

  return 0;
};

const isSkillTaxonomyKey = (k: string): k is SkillTaxonomySelectionListKey =>
  (SKILL_TAXONOMY_SELECTION_LIST_KEYS as readonly string[]).includes(k);

const getHumanReadableLevelId = (levelId: string): string | null => {
  if (levelId === 'role_k7') {
    return 'Job Category';
  }

  if (levelId.includes('_k')) {
    return levelId.split('_')[1] ?? null;
  }

  if (levelId === 'msa') {
    return 'Metro Area';
  }

  if (levelId === 'company') {
    return 'company';
  }

  if (isSkillTaxonomyKey(levelId)) {
    return null;
  }

  return levelId.charAt(0).toUpperCase() + levelId.slice(1);
};

export const FilterTag: React.FC<FilterTagProps> = ({
  filter,
  onRemoveFilter,
  disabled = false,
  hideDisabledTooltip = false,
}) => {
  const viewGranularity = useViewDateGranularity();
  const { availableDateRange } = useAvailableDateRangeWithTrialLag();
  const { labelsByDepth } = useCustomSkillLevelLabels(0);
  const { dispatch } = useFilterContext();
  const location = useLocation();
  const { currencySymbol, exchangeRate } = useCurrencyPreference();

  const textColor = 'text.primary';
  const valueColor = 'lightBlue.600';

  const filterChipName = (() => {
    const shouldShowCurrentPrevious = shouldShowCurrentPreviousToggle(
      filter.id,
      location.pathname
    );

    const name = filter.name;

    if (!shouldShowCurrentPrevious || !('isCurrent' in filter)) {
      return name;
    }

    const prefix = filter.isCurrent ? 'Current' : 'Previous';
    return `${prefix} ${name}`;
  })();

  const handleRemoveFilter = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isMultiFilterInstance(filter)) {
      dispatch({
        type: 'REMOVE_MULTI_FILTER_INSTANCE',
        payload: {
          parentId: filter.parentMultiFilterId,
          instanceIndex: filter.instanceIndex,
        },
      });
      return;
    }
    onRemoveFilter(filter.id);
  };

  const renderDateFilterTag = () => {
    if (
      !availableDateRange ||
      (filter.type !== FilterType.DATE_RANGE && filter.type !== FilterType.DATE)
    ) {
      return null;
    }

    if (!('min' in filter) || !('max' in filter)) {
      return null;
    }

    const startDate = filter.min;
    const endDate = filter.max;

    if (!startDate || !endDate) {
      return null;
    }

    const displayStartDate = getFormattedDateString(startDate, viewGranularity);
    const displayEndDate = getFormattedDateString(endDate, viewGranularity);

    if (filter.id === FilterId.SNAPSHOT_DATE) {
      return (
        <Text as="span" color={valueColor} fontWeight={600}>
          {displayStartDate}
        </Text>
      );
    }

    return (
      <HStack spacing={1} display="inline-flex">
        <Text as="span" color={valueColor} fontWeight={600}>
          {displayStartDate}
        </Text>
        <Text>to</Text>
        <Text as="span" color={valueColor} fontWeight={600}>
          {displayEndDate}
        </Text>
      </HStack>
    );
  };

  const filterValue = (() => {
    switch (filter.type) {
      case FilterType.DATE_RANGE:
      case FilterType.DATE:
        return renderDateFilterTag();
      case FilterType.RANGE:
        if ('min' in filter && 'max' in filter) {
          const min = filter.min;
          const max = filter.max;
          const isPayFilter = filter.id === FilterId.PAY;
          const filterIsFunding = isFundingFilterId(filter.id);
          const isRateFilter = isRateFilterId(filter.id);
          const formatValue = (value: number) => {
            return formatFilterCurrencyValue({
              value,
              exchangeRate,
              currencySymbol,
              filterId: filter.id,
              isPayFilter,
              isFundingFilter: filterIsFunding,
              isRateFilter,
            });
          };

          const isMinEffectivelySet = checkIsMinEffectivelySet(
            min,
            filter.id,
            isPayFilter,
            filterIsFunding
          );

          const maxEffectivelySet = isMaxEffectivelySet(
            max,
            filter.id,
            isMinEffectivelySet
          );

          if (isMinEffectivelySet && !maxEffectivelySet) {
            if (min === undefined) return null;
            return (
              <HStack spacing={1} display="inline-flex">
                <Text as="span">greater than</Text>
                <Text as="span" color={valueColor} fontWeight={600}>
                  {isPayFilter || filterIsFunding || isRateFilter
                    ? formatValue(min)
                    : min}
                </Text>
              </HStack>
            );
          }

          if (!isMinEffectivelySet && maxEffectivelySet) {
            if (max === undefined) return null;
            return (
              <HStack spacing={1} display="inline-flex">
                <Text as="span">less than</Text>
                <Text as="span" color={valueColor} fontWeight={600}>
                  {isPayFilter || filterIsFunding || isRateFilter
                    ? formatValue(max)
                    : max}
                </Text>
              </HStack>
            );
          }

          if (isMinEffectivelySet && maxEffectivelySet) {
            if (max === undefined || min === undefined) return null;
            if (min === max) {
              return (
                <Text as="span" color={valueColor} fontWeight={600}>
                  {isPayFilter || filterIsFunding || isRateFilter
                    ? formatValue(min)
                    : min}
                </Text>
              );
            }

            return (
              <HStack spacing={1} display="inline-flex">
                {!isYearFilter(filter.id) && <Text as="span">from</Text>}
                <Text as="span" color={valueColor} fontWeight={600}>
                  {isPayFilter || filterIsFunding || isRateFilter
                    ? formatValue(min)
                    : min}
                </Text>
                <Text as="span">to</Text>
                <Text as="span" color={valueColor} fontWeight={600}>
                  {isPayFilter || filterIsFunding || isRateFilter
                    ? formatValue(max)
                    : max}
                </Text>
              </HStack>
            );
          }

          if (!isMinEffectivelySet && !maxEffectivelySet) {
            if (min === undefined || max === undefined) return null;
            return (
              <HStack spacing={1} display="inline-flex">
                <Text as="span">less than</Text>
                <Text as="span" color={valueColor} fontWeight={600}>
                  {isPayFilter || filterIsFunding || isRateFilter
                    ? formatValue(max)
                    : max}
                </Text>
              </HStack>
            );
          }
        }
        return null;

      case FilterType.STATIC_LIST:
      case FilterType.LIST:
        if ('selections' in filter) {
          const displayName = formatValueList(
            filter.selections.map((s) => s.name)
          );

          if (displayName) {
            return (
              <Text as="span" color={valueColor} fontWeight={600}>
                {displayName}
              </Text>
            );
          }
        }
        return null;

      case FilterType.TREE:
        if ('selections' in filter) {
          const displayName = formatValueList(
            filter.selections.map((s) => s.name)
          );

          const displayLevelId = (() => {
            if (filter.id === FilterId.CUSTOM_SKILL) {
              const labels = Array.from(
                new Set(
                  filter.selections
                    .map((s) => {
                      const match = String(s.levelId).match(/^skill_(\d+)/);
                      const depth = match ? Number(match[1]) : null;
                      return depth !== null && depth !== undefined
                        ? (labelsByDepth?.[depth] ?? null)
                        : null;
                    })
                    .filter((v): v is string => Boolean(v))
                )
              );
              return labels.join('/');
            }

            const levelIds = Array.from(
              new Set(
                filter.selections
                  .map((s) => getHumanReadableLevelId(s.levelId))
                  .filter((v): v is string => v !== null)
              )
            );

            // For COMPANY/COMPETITORS filter: only show "company" if there are also industry selections
            if (
              filter.id === FilterId.COMPANY ||
              filter.id === FilterId.COMPETITORS ||
              filter.id === FilterId.FOUNDER_COMPANY
            ) {
              const hasIndustrySelections = filter.selections.some(
                (s) =>
                  s.levelId === 'rics_k10' ||
                  s.levelId === 'rics_k50' ||
                  s.levelId === 'rics_k400'
              );
              if (!hasIndustrySelections) {
                // Filter out 'company' from display if only companies are selected
                return levelIds.filter((id) => id !== 'company').join('/');
              }
            }

            return levelIds.join('/');
          })();

          if (displayName) {
            return (
              <Text as="span" color={valueColor} fontWeight={600}>
                {displayName}
                {displayLevelId ? ` (${displayLevelId})` : ''}
              </Text>
            );
          }
        }
        return null;

      case FilterType.TEXT:
        if ('value' in filter) {
          return (
            <Text as="span" color={valueColor} fontWeight={600}>
              {filter.value}
            </Text>
          );
        }
        return null;

      case FilterType.FREE_TEXT:
        if ('text' in filter) {
          const displayString = formatValueList(filter.text);

          if (displayString) {
            return (
              <Text as="span" color={valueColor} fontWeight={600}>
                {displayString}
              </Text>
            );
          }
        }
        return null;

      case FilterType.SCHOOL:
        if ('schoolResultItems' in filter) {
          const displayName = formatValueList(
            Object.values(filter.schoolResultItems).map((school) => school.name)
          );
          return (
            <Text as="span" color={valueColor} fontWeight={600}>
              {displayName}
            </Text>
          );
        }
        return null;

      default:
        return null;
    }
  })();

  const middleText = (() => {
    const isNot = 'isNot' in filter && filter.isNot === true;
    const notSuffix = isNot ? ' not' : '';

    if (filter.id === FilterId.SNAPSHOT_DATE || filter.id === FilterId.PAY) {
      return ` is${notSuffix} `;
    }

    if (filter.type === FilterType.DATE_RANGE) {
      return ` is${notSuffix} from `;
    }

    if (filter.type === FilterType.RANGE) {
      const { min, max } = filter;

      const isRateFilter = isRateFilterId(filter.id);
      const filterIsFunding = isFundingFilterId(filter.id);

      const isMinEffectivelySet = checkIsMinEffectivelySet(
        min,
        filter.id,
        false, // isPayFilter - PAY is not a RANGE type in this context
        filterIsFunding
      );
      const maxEffectivelySet = isMaxEffectivelySet(
        max,
        filter.id,
        isMinEffectivelySet
      );
      const isBetween = isMinEffectivelySet && maxEffectivelySet;

      // Less than case or graduation year: always use " is "
      if (
        filter.id === FilterId.GRADUATION_YEAR ||
        (isBetween && min === max)
      ) {
        return ` is${notSuffix} `;
      }

      // Less than case or greater than case: always use " is "
      if (
        (!isMinEffectivelySet && maxEffectivelySet) ||
        (isMinEffectivelySet && !maxEffectivelySet)
      ) {
        return ` is${notSuffix} `;
      }

      // Neither min nor max effectively set (default values): use " is "
      if (!isMinEffectivelySet && !maxEffectivelySet) {
        return ` is${notSuffix} `;
      }

      // Between case with different values: use " from " for rate filters, " is from " for others
      if (isRateFilter) {
        return ` from `;
      }
      return ` is${notSuffix} from `;
    }

    if (
      filter.type === FilterType.STATIC_LIST ||
      filter.type === FilterType.TREE ||
      filter.type === FilterType.LIST ||
      filter.type === FilterType.FREE_TEXT ||
      filter.type === FilterType.SCHOOL
    ) {
      const count = getFilterItemCount(filter);
      if (isNot) {
        return ' is not ';
      }
      return count > 1 ? ' is one of ' : ' is ';
    }

    return ` is${notSuffix} `;
  })();

  const renderTag = ({ isDisabled }: { isDisabled: boolean }) => {
    const baseTagProps = isDisabled
      ? {
          cursor: 'not-allowed',
          border: '1px solid',
          borderColor: 'gray.200',
          bg: 'gray.200',
          color: 'gray.500',
          opacity: 0.7,
        }
      : {
          cursor: 'pointer',
          border: '1px solid white',
          _hover: {
            bg: 'white',
            borderColor: 'gray.300',
            transition: 'all 0.2s ease-in-out',
          },
        };

    const tagProps = baseTagProps;

    return (
      <Tag
        size="sm"
        variant="solid"
        height="24px"
        borderRadius="4px"
        data-testid={`filter-chip-${filter.id}`}
        pointerEvents="auto"
        {...tagProps}
      >
        <Flex alignItems="center" minH="24px">
          <TagLabel>
            <HStack spacing={1}>
              <Text
                as="span"
                color={isDisabled ? 'gray.500' : textColor}
                fontWeight={600}
              >
                {filterChipName}
              </Text>
              <Text color="gray.500" fontSize="xs">
                •
              </Text>
              {middleText.trim() && (
                <>
                  <Text fontSize="xs">{middleText}</Text>
                  <Text color="gray.500" fontSize="xs">
                    •
                  </Text>
                </>
              )}
              {filterValue}
            </HStack>
          </TagLabel>
          <TagCloseButton
            onClick={handleRemoveFilter}
            data-testid="clear-filter-chip"
            ml={1}
            pointerEvents="auto"
          />
        </Flex>
      </Tag>
    );
  };

  const renderTrigger = (() => {
    if (disabled) {
      const tooltipProps = {
        label: 'This filter is unavailable on this page.',
        variant: 'label',
        hasArrow: true,
        openDelay: 375,
        isDisabled: hideDisabledTooltip,
      };
      return (
        <Tooltip {...tooltipProps}>{renderTag({ isDisabled: true })}</Tooltip>
      );
    }

    return renderTag({ isDisabled: false });
  })();

  return (
    filterValue !== null && (
      <FilterPopover
        filter={filter}
        trigger={<Box display="inline-block">{renderTrigger}</Box>}
      />
    )
  );
};
